/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { ProductLookupClause, ProductsDataActions, ProductSearchCriteria, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

import { IActiveImageConfig, IActivePointsData } from '@msdyn365-commerce-modules/active-image/src/modules/active-image';

/**
 * ActiveImageInput.
 */

export class ActiveImageInput implements IActionInput {
    public config: IActiveImageConfig;

    private readonly _cacheKey: string = 'ActiveImage';

    public constructor(config: IActiveImageConfig) {
        this.config = config;

        if (this.config.activePoints && ArrayExtensions.hasElements(this.config.activePoints)) {
            for (const activePointData of this.config.activePoints) {
                if (activePointData.itemId) {
                    this._cacheKey = `${this._cacheKey}-${activePointData.itemId.toString()}`;
                }
            }
        }
    }

    /**
     * GetCacheObjectType.
     * @returns - Returns string.
     */
    public getCacheObjectType = (): string => 'ActiveImageFullProductSearchResult';

    /**
     * GetCacheKey.
     * @returns - Returns string.
     */
    public getCacheKey = (): string => this._cacheKey;

    /**
     * DataCacheType.
     * @returns - CacheType string.
     */
    //public dataCacheType = (): CacheType => 'application';
    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput.
 * @param inputData - IActionContext.
 * @returns - IActionInput.
 */
export const createInput = (inputData: ICreateActionContext): IActionInput => {
    return new ActiveImageInput(inputData.config as IActiveImageConfig);
};

/**
 * Action.
 * @param input - ActiveImageInput.
 * @param context - IActionContext.
 * @returns - ProductSearchResult[].
 */
export const getActiveImageproductAction = async (input: ActiveImageInput, context: IActionContext): Promise<ProductSearchResult[]> => {
    const { config } = input;
    const itemIds: ProductLookupClause[] = [];
    const ids: number[] = [];
    if (config.activePoints) {
        config.activePoints.map(
            (activePointData: IActivePointsData) => !isNaN(Number(activePointData.itemId)) && ids.push(Number(activePointData.itemId))
        );
    }
    if (config.activePoints) {
        config.activePoints.map(
            (activePointData: IActivePointsData) =>
                isNaN(Number(activePointData.itemId)) && itemIds.push({ ItemId: activePointData.itemId })
        );
    }
    const searchCriteriaInput: ProductSearchCriteria = {};
    searchCriteriaInput.Context = {
        ChannelId: context.requestContext.apiSettings.channelId,
        CatalogId: getCatalogId(context.requestContext)
    };
    searchCriteriaInput.IncludeAttributes = true;
    //   searchCriteriaInput.Ids = ids;
    //   searchCriteriaInput.ItemIds = itemIds;
    if (!config.activePoints || !ArrayExtensions.hasElements(config.activePoints)) {
        throw new Error('Fail to call ActiveImage API no active points configured.');
    }
    const searchCriteriaInputById = {
        ...searchCriteriaInput,
        Ids: ids
    };
    const searchCriteriaInputByItemId = {
        ...searchCriteriaInput,
        ItemIds: itemIds
    };
    // eslint-disable-next-line no-return-await -- Disable because we want to return the promise.
    const searchById =
        ids.length > 0 ? await ProductsDataActions.searchByCriteriaAsync({ callerContext: context }, searchCriteriaInputById) : [];
    const searchByItemId =
        itemIds.length > 0 ? await ProductsDataActions.searchByCriteriaAsync({ callerContext: context }, searchCriteriaInputByItemId) : [];
    return [...searchById, ...searchByItemId];
};

/**
 * Action.
 * @param id - Id.
 * @param action - Action.
 * @returns - Results.
 */
export const actionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/active-image/get-active-image-products',
    action: getActiveImageproductAction as IAction<ProductSearchResult[]>,
    input: createInput
});

export default actionDataAction;
